import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Button } from "@veorest/ui";
import {
  deleteMarketplaceBanner,
  deleteMarketplaceCategory,
  deleteMarketplaceServiceItem,
  fetchMarketplaceAppSettings,
  saveMarketplaceBanner,
  saveMarketplaceBestSellers,
  saveMarketplaceCategory,
  saveMarketplaceSections,
  saveMarketplaceServiceItem,
  saveMarketplaceTopRestaurants,
  uploadMarketplaceAsset,
  type MarketplaceAppBanner,
  type MarketplaceAppBestSellerCandidate,
  type MarketplaceAppCategory,
  type MarketplaceAppSection,
  type MarketplaceAppServiceItem,
} from "@veorest/api-client";
import {
  Image as ImageIcon,
  LayoutGrid,
  Store,
  Plus,
  Trash2,
  Upload,
  Check,
  Headphones,
  Star,
  ListOrdered,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { PageHeader } from "../components/shell/PageHeader";
import { SegmentStrip } from "../components/data/SegmentStrip";

type TabKey =
  | "banners"
  | "categories"
  | "service"
  | "bestsellers"
  | "order"
  | "sections";

export default function AppSettingsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>("banners");
  const data = useQuery({
    queryKey: ["admin-marketplace-app"],
    queryFn: fetchMarketplaceAppSettings,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["admin-marketplace-app"] });

  return (
    <div className="menu-page">
      <PageHeader
        eyebrow="Customer app"
        title="App Settings"
        subtitle="Marketplace banners, food filters, and Top Restaurants for the customer Android home."
      />

      <SegmentStrip
        ariaLabel="App settings sections"
        value={tab}
        onChange={setTab}
        options={[
          { key: "banners", label: "Banner", icon: ImageIcon },
          { key: "categories", label: "Categories", icon: LayoutGrid },
          { key: "service", label: "Service Panel", icon: Headphones },
          { key: "bestsellers", label: "Best Sellers", icon: Star },
          { key: "order", label: "Home Order", icon: ListOrdered },
          { key: "sections", label: "Top Restaurants", icon: Store },
        ]}
      />

      {data.isLoading ? (
        <p className="mt-4 text-sm text-[var(--text-muted)]">Loading…</p>
      ) : data.isError ? (
        <p className="mt-4 text-sm text-red-600">
          {(data.error as Error).message || "Failed to load"}
        </p>
      ) : (
        <>
          {tab === "banners" && (
            <BannersTab
              banners={data.data?.banners ?? []}
              hint={data.data?.mediaHints.banner.note}
              onChanged={invalidate}
            />
          )}
          {tab === "categories" && (
            <CategoriesTab
              categories={data.data?.categories ?? []}
              hint={data.data?.mediaHints.category.note}
              onChanged={invalidate}
            />
          )}
          {tab === "service" && (
            <ServicePanelTab
              items={data.data?.serviceItems ?? []}
              onChanged={invalidate}
            />
          )}
          {tab === "bestsellers" && (
            <BestSellersTab
              selected={data.data?.bestSellers ?? []}
              candidates={data.data?.bestSellerCandidates ?? []}
              onChanged={invalidate}
            />
          )}
          {tab === "order" && (
            <HomeOrderTab
              sections={data.data?.sections ?? []}
              onChanged={invalidate}
            />
          )}
          {tab === "sections" && (
            <SectionsTab
              title={data.data?.topRestaurants.title ?? "Top Restaurants near you"}
              enabled={data.data?.topRestaurants.enabled ?? true}
              selectedIds={data.data?.topRestaurants.tenantIds ?? []}
              clients={data.data?.clients ?? []}
              onChanged={invalidate}
            />
          )}
        </>
      )}
    </div>
  );
}

function BannersTab({
  banners,
  hint,
  onChanged,
}: {
  banners: MarketplaceAppBanner[];
  hint?: string;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState({
    id: undefined as string | undefined,
    title: "",
    highlight: "",
    ctaLabel: "",
    imageUrl: "",
    sortOrder: 0,
    isActive: true,
  });
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      saveMarketplaceBanner({
        id: draft.id,
        title: draft.title || null,
        highlight: draft.highlight || null,
        ctaLabel: draft.ctaLabel || null,
        imageUrl: draft.imageUrl,
        sortOrder: draft.sortOrder,
        isActive: draft.isActive,
      }),
    onSuccess: () => {
      toast.success(draft.id ? "Banner updated" : "Banner added");
      setDraft({
        id: undefined,
        title: "",
        highlight: "",
        ctaLabel: "",
        imageUrl: "",
        sortOrder: banners.length,
        isActive: true,
      });
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteMarketplaceBanner(id),
    onSuccess: () => {
      toast.success("Banner removed");
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadMarketplaceAsset(file, "banner");
      setDraft((d) => ({ ...d, imageUrl: url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_320px]">
      <section className="overview-panel">
        <header className="overview-panel__header">
          <span className="overview-panel__icon" aria-hidden>
            <ImageIcon className="h-4 w-4" />
          </span>
          <div>
            <h3 className="overview-panel__title">{draft.id ? "Edit banner" : "Add banner"}</h3>
            <p className="overview-panel__subtitle">{hint || "1080×420 recommended"}</p>
          </div>
        </header>
        <div className="overview-panel__body space-y-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Title">
              <input
                className="input-base"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Special offers"
              />
            </Field>
            <Field label="Highlight">
              <input
                className="input-base"
                value={draft.highlight}
                onChange={(e) => setDraft({ ...draft, highlight: e.target.value })}
                placeholder="Up to 50% OFF"
              />
            </Field>
            <Field label="CTA label">
              <input
                className="input-base"
                value={draft.ctaLabel}
                onChange={(e) => setDraft({ ...draft, ctaLabel: e.target.value })}
                placeholder="Order now"
              />
            </Field>
            <Field label="Sort order">
              <input
                className="input-base"
                type="number"
                value={draft.sortOrder}
                onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) || 0 })}
              />
            </Field>
          </div>
          <Field label="Image URL">
            <div className="flex gap-2">
              <input
                className="input-base flex-1"
                value={draft.imageUrl}
                onChange={(e) => setDraft({ ...draft, imageUrl: e.target.value })}
                placeholder="https://…"
              />
              <Button
                type="button"
                variant="secondary"
                className="min-h-[40px] shrink-0"
                loading={uploading}
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="h-4 w-4" />
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => void onFile(e.target.files?.[0])}
              />
            </div>
          </Field>
          {draft.imageUrl ? (
            <img
              src={draft.imageUrl}
              alt=""
              className="h-28 w-full rounded-xl object-cover bg-[var(--surface-muted)]"
            />
          ) : null}
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
            />
            Active
          </label>
          <div className="flex gap-2">
            <Button
              className="min-h-[40px]"
              loading={save.isPending}
              disabled={!draft.imageUrl.trim()}
              onClick={() => save.mutate()}
            >
              {draft.id ? "Save banner" : "Add banner"}
            </Button>
            {draft.id ? (
              <Button
                variant="secondary"
                className="min-h-[40px]"
                onClick={() =>
                  setDraft({
                    id: undefined,
                    title: "",
                    highlight: "",
                    ctaLabel: "",
                    imageUrl: "",
                    sortOrder: banners.length,
                    isActive: true,
                  })
                }
              >
                Cancel
              </Button>
            ) : null}
          </div>
        </div>
      </section>

      <section className="overview-panel">
        <header className="overview-panel__header">
          <span className="overview-panel__icon" aria-hidden>
            <LayoutGrid className="h-4 w-4" />
          </span>
          <div>
            <h3 className="overview-panel__title">Live banners</h3>
            <p className="overview-panel__subtitle">{banners.length} configured</p>
          </div>
        </header>
        <div className="overview-panel__body divide-y divide-[var(--border-subtle)]">
          {banners.length === 0 ? (
            <p className="p-4 text-sm text-[var(--text-muted)]">No banners yet.</p>
          ) : (
            banners.map((b) => (
              <div key={b.id} className="flex items-center gap-3 p-3">
                <img
                  src={b.imageUrl}
                  alt=""
                  className="h-12 w-20 rounded-lg object-cover bg-[var(--surface-muted)]"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold">{b.title || "Untitled"}</p>
                  <p className="truncate text-[11px] text-[var(--text-muted)]">
                    {b.isActive ? "Active" : "Hidden"} · order {b.sortOrder}
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded-lg p-2 text-[var(--text-muted)] hover:bg-[var(--accent-light)] hover:text-[var(--accent)]"
                  onClick={() =>
                    setDraft({
                      id: b.id,
                      title: b.title ?? "",
                      highlight: b.highlight ?? "",
                      ctaLabel: b.ctaLabel ?? "",
                      imageUrl: b.imageUrl,
                      sortOrder: b.sortOrder,
                      isActive: b.isActive,
                    })
                  }
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="rounded-lg p-2 text-[var(--text-muted)] hover:bg-red-50 hover:text-red-600"
                  onClick={() => remove.mutate(b.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function CategoriesTab({
  categories,
  hint,
  onChanged,
}: {
  categories: MarketplaceAppCategory[];
  hint?: string;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState({
    id: undefined as string | undefined,
    name: "",
    nameAr: "",
    nameKu: "",
    imageUrl: "",
    matchKeywords: "",
    sortOrder: 0,
    isActive: true,
  });
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      saveMarketplaceCategory({
        id: draft.id,
        name: draft.name.trim(),
        nameAr: draft.nameAr || null,
        nameKu: draft.nameKu || null,
        imageUrl: draft.imageUrl,
        matchKeywords: draft.matchKeywords
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        sortOrder: draft.sortOrder,
        isActive: draft.isActive,
      }),
    onSuccess: () => {
      toast.success(draft.id ? "Category updated" : "Category added");
      setDraft({
        id: undefined,
        name: "",
        nameAr: "",
        nameKu: "",
        imageUrl: "",
        matchKeywords: "",
        sortOrder: categories.length,
        isActive: true,
      });
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteMarketplaceCategory(id),
    onSuccess: () => {
      toast.success("Category removed");
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadMarketplaceAsset(file, "category");
      setDraft((d) => ({ ...d, imageUrl: url }));
      toast.success("Icon uploaded");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_300px]">
      <section className="overview-panel">
        <header className="overview-panel__header">
          <span className="overview-panel__icon" aria-hidden>
            <LayoutGrid className="h-4 w-4" />
          </span>
          <div>
            <h3 className="overview-panel__title">
              {draft.id ? "Edit category" : "Add category"}
            </h3>
            <p className="overview-panel__subtitle">
              {hint || "Round icons in the customer app · 256×256"}
            </p>
          </div>
        </header>
        <div className="overview-panel__body space-y-3 p-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Name (EN)">
              <input
                className="input-base"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Burger"
              />
            </Field>
            <Field label="Name (AR)">
              <input
                className="input-base"
                value={draft.nameAr}
                onChange={(e) => setDraft({ ...draft, nameAr: e.target.value })}
                dir="rtl"
              />
            </Field>
            <Field label="Name (KU)">
              <input
                className="input-base"
                value={draft.nameKu}
                onChange={(e) => setDraft({ ...draft, nameKu: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Match keywords (comma-separated)">
            <input
              className="input-base"
              value={draft.matchKeywords}
              onChange={(e) => setDraft({ ...draft, matchKeywords: e.target.value })}
              placeholder="burger, burgers, hamburger"
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-[1fr_100px]">
            <Field label="Icon image URL">
              <div className="flex gap-2">
                <input
                  className="input-base flex-1"
                  value={draft.imageUrl}
                  onChange={(e) => setDraft({ ...draft, imageUrl: e.target.value })}
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-[40px] shrink-0"
                  loading={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload className="h-4 w-4" />
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => void onFile(e.target.files?.[0])}
                />
              </div>
            </Field>
            <Field label="Sort">
              <input
                className="input-base"
                type="number"
                value={draft.sortOrder}
                onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) || 0 })}
              />
            </Field>
          </div>
          {draft.imageUrl ? (
            <img
              src={draft.imageUrl}
              alt=""
              className="h-16 w-16 rounded-full object-cover bg-[var(--surface-muted)]"
            />
          ) : null}
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
            />
            Active
          </label>
          <Button
            className="min-h-[40px]"
            loading={save.isPending}
            disabled={!draft.name.trim() || !draft.imageUrl.trim()}
            onClick={() => save.mutate()}
          >
            <Plus className="h-4 w-4" />
            {draft.id ? "Save category" : "Add category"}
          </Button>
        </div>
      </section>

      <section className="overview-panel">
        <header className="overview-panel__header">
          <span className="overview-panel__icon" aria-hidden>
            <Check className="h-4 w-4" />
          </span>
          <div>
            <h3 className="overview-panel__title">Food filters</h3>
            <p className="overview-panel__subtitle">{categories.length} categories</p>
          </div>
        </header>
        <div className="overview-panel__body divide-y divide-[var(--border-subtle)]">
          {categories.length === 0 ? (
            <p className="p-4 text-sm text-[var(--text-muted)]">No categories yet.</p>
          ) : (
            categories.map((c) => (
              <div key={c.id} className="flex items-center gap-3 p-3">
                <img
                  src={c.imageUrl}
                  alt=""
                  className="h-11 w-11 rounded-full object-cover bg-[var(--surface-muted)]"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold">{c.name}</p>
                  <p className="truncate text-[11px] text-[var(--text-muted)]">
                    {(c.matchKeywords || []).slice(0, 3).join(", ") || "No keywords"}
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded-lg px-2 py-1 text-xs font-bold text-[var(--accent)]"
                  onClick={() =>
                    setDraft({
                      id: c.id,
                      name: c.name,
                      nameAr: c.nameAr ?? "",
                      nameKu: c.nameKu ?? "",
                      imageUrl: c.imageUrl,
                      matchKeywords: (c.matchKeywords || []).join(", "),
                      sortOrder: c.sortOrder,
                      isActive: c.isActive,
                    })
                  }
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="rounded-lg p-2 text-[var(--text-muted)] hover:bg-red-50 hover:text-red-600"
                  onClick={() => remove.mutate(c.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

const SERVICE_ICON_KEYS = ["schedule", "payment", "verified", "favorite", "star"];

const SERVICE_DRAFT_EMPTY = {
  id: "",
  iconKey: "schedule",
  label: "",
  labelAr: "",
  labelKu: "",
  isActive: true,
};

function ServicePanelTab({
  items,
  onChanged,
}: {
  items: MarketplaceAppServiceItem[];
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState({ ...SERVICE_DRAFT_EMPTY });

  const save = useMutation({
    mutationFn: () =>
      saveMarketplaceServiceItem({
        id: draft.id || undefined,
        iconKey: draft.iconKey,
        label: draft.label.trim(),
        labelAr: draft.labelAr.trim() || null,
        labelKu: draft.labelKu.trim() || null,
        sortOrder: draft.id ? undefined : items.length,
        isActive: draft.isActive,
      }),
    onSuccess: () => {
      toast.success(draft.id ? "Item updated" : "Item added");
      setDraft({ ...SERVICE_DRAFT_EMPTY });
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteMarketplaceServiceItem(id),
    onSuccess: () => {
      toast.success("Item removed");
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-[380px_1fr]">
      <section className="overview-panel">
        <header className="overview-panel__header">
          <span className="overview-panel__icon" aria-hidden>
            <Headphones className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="overview-panel__title">{draft.id ? "Edit item" : "Add item"}</h3>
            <p className="overview-panel__subtitle">
              Renders inside the service panel under the categories.
            </p>
          </div>
        </header>
        <div className="overview-panel__body space-y-3 p-4">
          <Field label="Icon">
            <select
              className="input-base"
              value={draft.iconKey}
              onChange={(e) => setDraft((d) => ({ ...d, iconKey: e.target.value }))}
            >
              {SERVICE_ICON_KEYS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Label (English)">
            <input
              className="input-base"
              value={draft.label}
              onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
            />
          </Field>
          <Field label="Label (Arabic)">
            <input
              className="input-base"
              dir="rtl"
              value={draft.labelAr}
              onChange={(e) => setDraft((d) => ({ ...d, labelAr: e.target.value }))}
            />
          </Field>
          <Field label="Label (Kurdish Sorani)">
            <input
              className="input-base"
              dir="rtl"
              value={draft.labelKu}
              onChange={(e) => setDraft((d) => ({ ...d, labelKu: e.target.value }))}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
            />
            Active
          </label>
          <div className="flex gap-2">
            <Button
              className="min-h-[40px]"
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              {draft.id ? "Save item" : "Add item"}
            </Button>
            {draft.id && (
              <button
                type="button"
                className="text-sm font-semibold text-[var(--text-muted)]"
                onClick={() => setDraft({ ...SERVICE_DRAFT_EMPTY })}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="overview-panel">
        <header className="overview-panel__header">
          <div className="min-w-0 flex-1">
            <h3 className="overview-panel__title">Live items</h3>
            <p className="overview-panel__subtitle">{items.length} configured</p>
          </div>
        </header>
        {items.length === 0 ? (
          <p className="p-4 text-sm text-[var(--text-muted)]">No items yet.</p>
        ) : (
          <ul className="divide-y divide-[var(--border-subtle)]">
            {items.map((item) => (
              <li key={item.id} className="flex items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">
                    {item.label} {!item.isActive && <span className="text-[var(--text-muted)]">(hidden)</span>}
                  </p>
                  <p className="truncate text-xs text-[var(--text-muted)]">
                    {item.iconKey} · {item.labelAr ?? "—"} · {item.labelKu ?? "—"}
                  </p>
                </div>
                <button
                  type="button"
                  className="text-sm font-semibold text-[var(--accent)]"
                  onClick={() =>
                    setDraft({
                      id: item.id,
                      iconKey: item.iconKey,
                      label: item.label,
                      labelAr: item.labelAr ?? "",
                      labelKu: item.labelKu ?? "",
                      isActive: item.isActive,
                    })
                  }
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="text-[var(--danger)]"
                  onClick={() => remove.mutate(item.id)}
                  aria-label={`Remove ${item.label}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function BestSellersTab({
  selected,
  candidates,
  onChanged,
}: {
  selected: Array<{ id: string }>;
  candidates: MarketplaceAppBestSellerCandidate[];
  onChanged: () => void;
}) {
  const [picked, setPicked] = useState<string[]>(selected.map((s) => s.id));
  const [q, setQ] = useState("");

  useEffect(() => {
    setPicked(selected.map((s) => s.id));
  }, [selected]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return candidates;
    return candidates.filter(
      (c) =>
        c.name.toLowerCase().includes(needle) ||
        c.restaurantName.toLowerCase().includes(needle)
    );
  }, [candidates, q]);

  const save = useMutation({
    mutationFn: () => saveMarketplaceBestSellers({ menuItemIds: picked }),
    onSuccess: () => {
      toast.success("Best sellers saved");
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = (id: string) =>
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 24) {
        toast.error("Max 24 items");
        return prev;
      }
      return [...prev, id];
    });

  return (
    <section className="overview-panel mt-4">
      <header className="overview-panel__header">
        <span className="overview-panel__icon" aria-hidden>
          <Star className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="overview-panel__title">Best sellers</h3>
          <p className="overview-panel__subtitle">
            Pinned catalogue items · price and photo always come from the live menu
          </p>
        </div>
        <Button className="min-h-[40px]" loading={save.isPending} onClick={() => save.mutate()}>
          Save section
        </Button>
      </header>
      <div className="overview-panel__body space-y-4 p-4">
        <p className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
          Selected ({picked.length})
        </p>
        <Field label="Search catalogue">
          <input
            className="input-base"
            value={q}
            placeholder="Item or restaurant name"
            onChange={(e) => setQ(e.target.value)}
          />
        </Field>
        <div className="max-h-[420px] overflow-auto rounded-xl border border-[var(--border-subtle)]">
          {filtered.length === 0 ? (
            <p className="p-4 text-sm text-[var(--text-muted)]">
              No catalogue items available.
            </p>
          ) : (
            <ul className="divide-y divide-[var(--border-subtle)]">
              {filtered.map((c) => (
                <li key={c.id} className="flex items-center gap-3 p-3">
                  <input
                    type="checkbox"
                    checked={picked.includes(c.id)}
                    onChange={() => toggle(c.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{c.name}</p>
                    <p className="truncate text-xs text-[var(--text-muted)]">
                      {c.restaurantName} · {c.price} {c.currency}
                      {!c.isAvailable && " · unavailable"}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function HomeOrderTab({
  sections,
  onChanged,
}: {
  sections: MarketplaceAppSection[];
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<MarketplaceAppSection[]>(sections);

  useEffect(() => {
    setRows(sections);
  }, [sections]);

  const move = (index: number, delta: number) =>
    setRows((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const moved = prev[index];
      if (!moved) return prev;
      const next = [...prev];
      next.splice(index, 1);
      next.splice(target, 0, moved);
      return next;
    });

  const save = useMutation({
    mutationFn: () =>
      saveMarketplaceSections({
        sections: rows.map((r, i) => ({
          key: r.key,
          title: r.title,
          enabled: r.enabled,
          sortOrder: i,
        })),
      }),
    onSuccess: () => {
      toast.success("Home order saved");
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section className="overview-panel mt-4">
      <header className="overview-panel__header">
        <span className="overview-panel__icon" aria-hidden>
          <ListOrdered className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="overview-panel__title">Home sections</h3>
          <p className="overview-panel__subtitle">
            Order and visibility of every section on the customer home screen.
          </p>
        </div>
        <Button className="min-h-[40px]" loading={save.isPending} onClick={() => save.mutate()}>
          Save order
        </Button>
      </header>
      <ul className="divide-y divide-[var(--border-subtle)]">
        {rows.map((row, index) => (
          <li key={row.key} className="flex flex-wrap items-center gap-3 p-3">
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label={`Move ${row.key} up`}
                disabled={index === 0}
                onClick={() => move(index, -1)}
                className={index === 0 ? "opacity-30" : ""}
              >
                <ArrowUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label={`Move ${row.key} down`}
                disabled={index === rows.length - 1}
                onClick={() => move(index, 1)}
                className={index === rows.length - 1 ? "opacity-30" : ""}
              >
                <ArrowDown className="h-4 w-4" />
              </button>
            </div>
            <span className="w-40 truncate text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {row.key}
            </span>
            <input
              className="input-base flex-1"
              value={row.title}
              onChange={(e) =>
                setRows((prev) =>
                  prev.map((r) => (r.key === row.key ? { ...r, title: e.target.value } : r))
                )
              }
            />
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                checked={row.enabled}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r) => (r.key === row.key ? { ...r, enabled: e.target.checked } : r))
                  )
                }
              />
              Visible
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SectionsTab({
  title,
  enabled,
  selectedIds,
  clients,
  onChanged,
}: {
  title: string;
  enabled: boolean;
  selectedIds: string[];
  clients: Array<{ id: string; name: string; logoUrl: string | null; status: string }>;
  onChanged: () => void;
}) {
  const [sectionTitle, setSectionTitle] = useState(title);
  const [isEnabled, setIsEnabled] = useState(enabled);
  const [picked, setPicked] = useState<string[]>(selectedIds);
  const [q, setQ] = useState("");

  useEffect(() => {
    setSectionTitle(title);
    setIsEnabled(enabled);
    setPicked(selectedIds);
  }, [title, enabled, selectedIds]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return clients;
    return clients.filter((c) => c.name.toLowerCase().includes(needle));
  }, [clients, q]);

  const save = useMutation({
    mutationFn: () =>
      saveMarketplaceTopRestaurants({
        title: sectionTitle.trim() || "Top Restaurants near you",
        enabled: isEnabled,
        tenantIds: picked,
      }),
    onSuccess: () => {
      toast.success("Sections saved");
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = (id: string) => {
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 24) {
        toast.error("Max 24 restaurants");
        return prev;
      }
      return [...prev, id];
    });
  };

  return (
    <section className="overview-panel mt-4">
      <header className="overview-panel__header">
        <span className="overview-panel__icon" aria-hidden>
          <Store className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="overview-panel__title">Top Restaurants</h3>
          <p className="overview-panel__subtitle">
            Shows directly under the banner · logos only · 2 rows × 3 max, horizontal scroll
          </p>
        </div>
        <Button className="min-h-[40px]" loading={save.isPending} onClick={() => save.mutate()}>
          Save section
        </Button>
      </header>
      <div className="overview-panel__body space-y-4 p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <Field label="Section title">
            <input
              className="input-base"
              value={sectionTitle}
              onChange={(e) => setSectionTitle(e.target.value)}
              placeholder="Top Restaurants near you"
            />
          </Field>
          <label className="mb-1 flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
            />
            Enabled
          </label>
        </div>

        <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)]/40 p-3">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
            Selected ({picked.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {picked.length === 0 ? (
              <span className="text-sm text-[var(--text-muted)]">None selected</span>
            ) : (
              picked.map((id) => {
                const c = clients.find((x) => x.id === id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggle(id)}
                    className="inline-flex items-center gap-2 rounded-lg bg-white px-2 py-1.5 text-xs font-semibold ring-1 ring-[var(--border-subtle)]"
                  >
                    {c?.logoUrl ? (
                      <img src={c.logoUrl} alt="" className="h-6 w-6 rounded-md object-cover" />
                    ) : (
                      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[var(--accent-light)] text-[10px] font-black text-[var(--accent)]">
                        {(c?.name || "?").slice(0, 1)}
                      </span>
                    )}
                    {c?.name || id.slice(0, 6)}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <Field label="Search restaurants">
          <input
            className="input-base max-w-md"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a restaurant…"
          />
        </Field>

        <div className="grid max-h-[420px] gap-2 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => {
            const on = picked.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggle(c.id)}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                  on
                    ? "bg-[var(--accent-light)] ring-1 ring-[var(--accent)]"
                    : "bg-white ring-1 ring-[var(--border-subtle)] hover:bg-[var(--surface-muted)]"
                }`}
              >
                {c.logoUrl ? (
                  <img src={c.logoUrl} alt="" className="h-10 w-10 rounded-lg object-cover" />
                ) : (
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--surface-muted)] text-sm font-black text-[var(--text-muted)]">
                    {c.name.slice(0, 1)}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold">{c.name}</span>
                  <span className="text-[11px] text-[var(--text-muted)]">{c.status}</span>
                </span>
                {on ? <Check className="h-4 w-4 shrink-0 text-[var(--accent)]" /> : null}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-bold text-[var(--text-secondary)]">{label}</span>
      {children}
    </label>
  );
}
